import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Datagrid,
  FunctionField,
  ListBase,
  ListToolbar,
  Pagination,
  Resource,
  TextField,
} from 'react-admin';

import ExportReportToolbar, { BuildExportUrl } from '../../componets/SubscriberManagementReport/ExportReportToolbar';
import FilterReports from '../../componets/SubscriberManagementReport/FilterReports';
import FilterChips from '../../componets/Ats/SuperAdminInbox/FilterChips';
import { buildDefaultDateFilters } from '../../utils/dateRangePresets';
import { ATS_API_URL } from '../../config';

interface PlaidManagementRow {
  id: string | number;
  subscriberName: string;
  locationName: string;
  numberOfUnits: number | null | string;
  plaidApplicationCount: number | null;
  bankLinkRequestCount: number | null;
  payrollLinkRequestCount: number | null;
  mostRecentVerifiedDate: string | null;
}

const DEFAULT_PAD_LENGTH = 5;

const formatNumber = (
  value: number | null | undefined | string,
  padLength: number = DEFAULT_PAD_LENGTH,
) => {
  const applyPadding = (input: string) => input.padStart(padLength, ' ');

  if (typeof value === 'number') {
    return applyPadding(new Intl.NumberFormat('en-US').format(value));
  }

  return applyPadding('-');
};

const formatRecentVerifiedDate = (value: string | null | undefined) => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  return '-';
};

const PlaidManagementDatagrid = () => (
  <Datagrid
    bulkActionButtons={false}
    sx={{
      '& .RaDatagrid-headerCell': {
        fontWeight: 'bold',
        fontSize: 12,
      },
      '& .RaDatagrid-rowEven': {
        backgroundColor: '#F9F9F9',
      },
      '& .MuiTableCell-root': {
        fontSize: 12,
      },
      '& th:nth-of-type(3),& td:nth-of-type(3),& th:nth-of-type(4),& td:nth-of-type(4),& th:nth-of-type(5),& td:nth-of-type(5),& th:nth-of-type(6),& td:nth-of-type(6)': {
        textAlign: 'left',
      },
    }}
    empty={
      <Box py={6} textAlign="center">
        <Typography>No Direct Linking verification records found.</Typography>
      </Box>
    }
  >
    <TextField source="subscriberName" label="Subscriber Name" />
    <TextField source="locationName" label="Property / Office Name" />
    <FunctionField
      label="Number of Units"
      sortBy="numberOfUnits"
      sortByOrder="DESC"
      render={(record: PlaidManagementRow) => (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            justifyContent: 'flex-end',
            minWidth: `${DEFAULT_PAD_LENGTH}ch`,
            whiteSpace: 'pre',
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'left',
          }}
        >
          {formatNumber(record.numberOfUnits)}
        </Box>
      )}
    />
    <FunctionField
      label="VR/ATS Apps Using Direct Linking"
      sortBy="plaidApplicationCount"
      sortByOrder="DESC"
      render={(record: PlaidManagementRow) => (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            justifyContent: 'flex-end',
            minWidth: `${DEFAULT_PAD_LENGTH}ch`,
            whiteSpace: 'pre',
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'left',
          }}
        >
          {formatNumber(record.plaidApplicationCount)}
        </Box>
      )}
    />
    <FunctionField
      label="Bank Link Requests"
      sortBy="bankLinkRequestCount"
      sortByOrder="DESC"
      render={(record: PlaidManagementRow) => (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            justifyContent: 'flex-end',
            minWidth: `${DEFAULT_PAD_LENGTH}ch`,
            whiteSpace: 'pre',
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'left',
          }}
        >
          {formatNumber(record.bankLinkRequestCount)}
        </Box>
      )}
    />
    <FunctionField
      label="Payroll Link Requests"
      sortBy="payrollLinkRequestCount"
      sortByOrder="DESC"
      render={(record: PlaidManagementRow) => (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            justifyContent: 'flex-end',
            minWidth: `${DEFAULT_PAD_LENGTH}ch`,
            whiteSpace: 'pre',
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'left',
          }}
        >
          {formatNumber(record.payrollLinkRequestCount)}
        </Box>
      )}
    />
    <FunctionField
      label="Most Recent Link Request"
      sortBy="mostRecentVerifiedDate"
      sortByOrder="DESC"
      render={(record: PlaidManagementRow) => formatRecentVerifiedDate(record.mostRecentVerifiedDate)}
    />
  </Datagrid>
);

const MyList = ({ children, filters, actions, ...props }: any) => (
  <ListBase {...props} perPage={100}>
    <ListToolbar filters={filters} actions={actions} />
    <FilterChips excludeKeys={['app_invitation_type', 'rangefrom', 'rangeto', 'subscriberId', 'subscriberExact']} />
    {children}
    <Pagination rowsPerPageOptions={[100, 200, 300, 400, 500]} />
    <br />
    <br />
    <br />
    <br />
  </ListBase>
);

const AdminPlaidManagementList = () => {
  const defaultFilters = useMemo(buildDefaultDateFilters, []);

  const buildExportUrl: BuildExportUrl = ({ filterValues, sort, total }) => {
    if (!ATS_API_URL) {
      return '';
    }

    const query: Record<string, string> = {};

    Object.entries(filterValues || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query[key] = String(value);
      }
    });

    if (sort?.field) {
      query._sort = sort.field;
    }

    if (sort?.order) {
      query._order = sort.order;
    }

    delete query._start;
    delete query._end;
    delete query.page;

    const safeTotal = typeof total === 'number' && total > 0 ? total : 1;
    query._start = '0';
    query._end = String(safeTotal);

    const params = new URLSearchParams(query);
    const queryString = params.toString();
    return `${ATS_API_URL}/plaid/management-report/export${queryString ? `?${queryString}` : ''}`;
  };

  return (
    <>
      <br />
      <br />
      <MyList
        sort={{ field: 'subscriberName', order: 'ASC' }}
        filters={<FilterReports />}
        actions={<ExportReportToolbar buildExportUrl={buildExportUrl} />}
        filterDefaultValues={defaultFilters}
      >
        <PlaidManagementDatagrid />
      </MyList>
    </>
  );
};

const AdminPlaidManagement = () => (
  <Resource name="plaidManagementReport" list={AdminPlaidManagementList} />
);

export default AdminPlaidManagement;
